// src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuStructure } from '../data/menuStructure';
import "./Sidebar.css";

const Sidebar = () => {
  const [expandedItems, setExpandedItems] = useState({});
  const [currentMainMenu, setCurrentMainMenu] = useState('');
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // 현재 경로를 기반으로 메인 메뉴와 확장 상태 결정
  useEffect(() => {
    const currentPath = location.pathname;
    
    // 현재 경로에 해당하는 메인 메뉴 찾기
    let foundMainMenu = '';
    let newExpandedItems = {};
    
    Object.entries(menuStructure).forEach(([menuKey, menu]) => {
      if (currentPath.startsWith(menu.path) && menu.path !== '/') {
        foundMainMenu = menuKey;
        
        // 서브메뉴가 있는 경우에만 사이드바 표시
        if (menu.submenus && menu.submenus.length > 0) {
          setSidebarVisible(true);
          
          // 현재 경로에 해당하는 서브메뉴 항목 확장
          const expandSubMenus = (submenus, parentId = '') => {
            submenus.forEach(submenu => {
              if (currentPath.startsWith(submenu.path)) {
                const itemId = parentId ? `${parentId}-${submenu.id}` : submenu.id;
                newExpandedItems[itemId] = true;
                
                if (submenu.submenus) {
                  expandSubMenus(submenu.submenus, itemId);
                }
              }
            });
          };
          
          expandSubMenus(menu.submenus);
        } else {
          setSidebarVisible(false);
        }
      }
    });
    
    // 홈페이지나 메인 메뉴가 없으면 사이드바 숨김
    if (currentPath === '/' || !foundMainMenu) {
      setSidebarVisible(false);
    }
    
    setCurrentMainMenu(foundMainMenu);
    setExpandedItems(newExpandedItems);
  }, [location.pathname]);

  // 현재 경로가 해당 메뉴와 일치하는지 확인
  const isActivePath = (path) => {
    return location.pathname === path;
  };

  // 서브메뉴 토글
  const toggleSubMenu = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // 메뉴 클릭 핸들러
  const handleMenuClick = (path, itemId = null) => {
    navigate(path);
    
    // 서브메뉴가 있는 항목이면 토글만 하고 네비게이션하지 않음
    if (itemId && hasSubMenus(itemId)) {
      toggleSubMenu(itemId);
    }
  };

  // 해당 항목이 서브메뉴를 가지고 있는지 확인
  const hasSubMenus = (itemId) => {
    const menu = menuStructure[currentMainMenu];
    if (!menu) return false;

    const findItem = (items) => {
      for (const item of items) {
        if (item.id === itemId) {
          return item.submenus && item.submenus.length > 0;
        }
        if (item.submenus) {
          const found = findItem(item.submenus);
          if (found !== undefined) return found;
        }
      }
      return false;
    };

    return findItem(menu.submenus);
  };

  // 서브메뉴 아이템 렌더링 (재귀)
  const renderMenuItems = (items, level = 0, parentId = '') => {
    return items.map((item) => {
      const itemId = parentId ? `${parentId}-${item.id}` : item.id;
      const hasSubItems = item.submenus && item.submenus.length > 0;
      const isExpanded = expandedItems[itemId];
      const isActive = isActivePath(item.path);
      const indentLevel = level * 20;

      return (
        <div key={itemId} className="menu-item-container">
          <div
            className={`menu-item ${isActive ? 'active' : ''} ${hasSubItems ? 'has-submenu' : ''}`}
            style={{ paddingLeft: `${20 + indentLevel}px` }}
            onClick={() => handleMenuClick(item.path, hasSubItems ? itemId : null)}
          >
            <div className="menu-item-content">
              {/* 아이콘 영역 */}
              <div className="menu-icon">
                {level === 0 ? (
                  getMenuIcon(item.id)
                ) : level === 1 ? (
                  <span className="sub-icon">▶</span>
                ) : (
                  <span className="sub-sub-icon">•</span>
                )}
              </div>
              
              {/* 메뉴 텍스트 */}
              <span className="menu-text">{item.name}</span>
              
              {/* 확장 아이콘 */}
              {hasSubItems && (
                <div className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                  <span>▼</span>
                </div>
              )}
            </div>
            
            {/* 활성 인디케이터 */}
            {isActive && <div className="active-indicator" />}
          </div>

          {/* 서브메뉴 */}
          {hasSubItems && (
            <div className={`submenu-container ${isExpanded ? 'expanded' : 'collapsed'}`}>
              <div className="submenu-content">
                {renderMenuItems(item.submenus, level + 1, itemId)}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  // 메뉴별 아이콘 반환
  const getMenuIcon = (menuId) => {
    const iconMap = {
      'overview': '📖',
      'sensor-config': '🔧',
      'acquisition-status': '📊',
      'upload': '⬆️',
      'download': '⬇️',
      'data-management': '🗃️',
      'processing-guideline': '📋',
      'processing': '⚙️',
      'db-construction': '🏗️',
      'cognitive-algorithm': '🧠',
      'evaluation-standard': '📏',
      'result-report': '📄',
      'usecase-scenario': '📝',
      'scenario-analysis': '🔍',
      'scenario-development': '🚀',
      'year1': '1️⃣',
      'year2': '2️⃣',
      'year3': '3️⃣',
      'usecase': '💼',
      'scenario': '🎬'
    };
    return <span className="menu-emoji">{iconMap[menuId] || '📂'}</span>;
  };

  // 빈 상태 렌더링
  const renderEmptyState = () => (
    <div className="empty-state">
      <div className="empty-icon">📂</div>
      <h3>메뉴를 선택해주세요</h3>
      <p>상단 메뉴에서 항목을 선택하면<br />상세 메뉴가 여기에 표시됩니다.</p>
    </div>
  );

  // 사이드바가 보이지 않으면 렌더링하지 않음
  if (!sidebarVisible || !currentMainMenu) {
    return null;
  }

  const currentMenu = menuStructure[currentMainMenu];

  return (
    <aside className="sidebar">
      {/* 사이드바 헤더 */}
      <div className="sidebar-header">
        <div className="header-content">
          <div className="main-menu-icon">
            {getMainMenuIcon(currentMainMenu)}
          </div>
          <div className="header-text">
            <h2 className="sidebar-title">{currentMenu.name}</h2>
            <p className="sidebar-subtitle">상세 메뉴</p>
          </div>
        </div>
        
        {/* 접기 버튼 
        <button 
          className="collapse-btn"
          onClick={() => setSidebarVisible(false)}
          title="사이드바 접기"
        >
          ◀
        </button>
        */}
      </div>

      {/* 메뉴 리스트 */}
      <div className="sidebar-content">
        <div className="menu-list">
          {currentMenu.submenus && currentMenu.submenus.length > 0 ? (
            renderMenuItems(currentMenu.submenus)
          ) : (
            renderEmptyState()
          )}
        </div>
      </div>

      {/* 사이드바 푸터 */}
      <div className="sidebar-footer">
        <div className="current-path">
          <span className="path-label">현재 위치:</span>
          <span className="path-text">{location.pathname}</span>
        </div>
      </div>
    </aside>
  );

  // 메인 메뉴별 아이콘 반환
  function getMainMenuIcon(menuKey) {
    const mainIconMap = {
      'Overview': '📋',
      'SG': '🎬',
      'DA': '📡',
      'ED': '⚙️',
      'EA': '🧠',
      'Standard': '📏',
      'Support': '🛠️',
      'Profile': '👤'
    };
    return <span className="main-menu-emoji">{mainIconMap[menuKey] || '📁'}</span>;
  }
};

export default Sidebar;